import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useFplId } from "../context/FplIdContext";
import {
  fetchFPLData,
  fetchInjuryNews,
  fetchFplLive,
} from "../api/fplApi";

/**
 * App-wide notification bell.
 * Computes in-app alerts from data you already fetch:
 *   - GW deadline (countdown, urgent under a few hours)
 *   - Injured / flagged players IN THE USER'S squad (needs their picks)
 *   - (extensible) price changes, captain issues, etc.
 *
 * No push infrastructure — this shows while the app is open. Real web-push
 * can be layered on later reusing the same alert-building logic.
 *
 * Dismissals are remembered (localStorage) so a closed alert stays closed
 * until its content changes.
 */

const DISMISS_KEY = "fpl_dismissed_alerts";

function loadDismissed() {
  try {
    return JSON.parse(localStorage.getItem(DISMISS_KEY) || "{}");
  } catch {
    return {};
  }
}
function saveDismissed(obj) {
  try {
    localStorage.setItem(DISMISS_KEY, JSON.stringify(obj));
  } catch {}
}

export default function NotificationBell() {
  const { fplId } = useFplId();
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [dismissed, setDismissed] = useState(loadDismissed());
  const ref = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  // Build alerts
  useEffect(() => {
    let alive = true;
    (async () => {
      const next = [];

      // ---- 1. Deadline alert (works for everyone, even pre-season) ----
      let gw = null;
      let squadGw = null;
      let deadline = null;
      try {
        const boot = await fetchFPLData();
        const events = boot?.events || boot?.data?.events || [];
        const current = events.find((e) => e.is_current);
        const started = events
          .filter((e) => e?.deadline_time && new Date(e.deadline_time).getTime() <= Date.now())
          .sort((a, b) => Number(b.id) - Number(a.id));
        const upcoming = events.find((e) => e.is_next) || events.find((e) => e?.deadline_time && new Date(e.deadline_time).getTime() > Date.now()) || current;
        gw = upcoming?.id ?? null;
        squadGw = current?.id || started[0]?.id || null;
        deadline = upcoming?.deadline_time ?? null;
        if (deadline) {
          const ms = new Date(deadline).getTime() - Date.now();
          const hrs = ms / 3600000;
          if (ms > 0 && hrs <= 72) {
            const urgent = hrs <= 4;
            const label =
              hrs < 1
                ? `${Math.max(1, Math.round(ms / 60000))} min`
                : hrs < 24
                ? `${Math.round(hrs)} hours`
                : `${Math.round(hrs / 24)} day(s)`;
            next.push({
              id: `deadline-gw${gw}`,
              kind: urgent ? "urgent" : "info",
              icon: "⏰",
              title: `GW${gw} deadline in ${label}`,
              body: urgent
                ? "Set your team and captain now — don't miss out!"
                : "Get your transfers and captain sorted.",
              action: () => navigate("/my-team"),
            });
          }
        }
      } catch {}

      // ---- 2. Injury / flagged players in the user's squad ----
      // Only possible if we have an id AND the squad exists (i.e. season started)
      if (fplId && squadGw) {
        try {
          const live = await fetchFplLive(fplId, squadGw);
          const picks = live?.squad ?? live?.xi_after_autosubs ?? live?.players ?? live?.picks ?? live?.elements ?? [];
          if (picks.length && !live?.error) {
            const injuries = await fetchInjuryNews();
            const injList = Array.isArray(injuries)
              ? injuries
              : injuries?.data || injuries?.elements || [];

            // index injuries by player id and by name for resilient matching
            const byId = {};
            const byName = {};
            injList.forEach((it) => {
              const pid = it.id ?? it.element ?? it.player_id;
              const nm = (it.web_name || it.name || it.player_name || "").toLowerCase();
              const status = it.status || it.availability;
              const news = it.news || it.reason || "";
              const chance =
                it.chance_of_playing_next_round ??
                it.chance_of_playing_this_round ??
                null;
              const rec = { status, news, chance };
              if (pid != null) byId[pid] = rec;
              if (nm) byName[nm] = rec;
            });

            picks.forEach((p) => {
              const pid = p.id ?? p.element;
              const nm = (p.name || p.web_name || p.player_name || "").toLowerCase();
              const info = byId[pid] || byName[nm];
              // flagged if status isn't "a" (available), or a news string exists
              const flagged =
                info && ((info.status && info.status !== "a") || info.news);
              if (flagged) {
                const chanceTxt =
                  info.chance != null ? ` · ${info.chance}% chance to play` : "";
                next.push({
                  id: `inj-${pid}-${info.news || info.status}`,
                  kind: "warn",
                  icon: "⚠️",
                  title: `${p.name || p.web_name} is ${info.status === "i" ? "injured" : info.status === "d" ? "doubtful" : info.status === "s" ? "suspended" : "unavailable"}`,
                  body: (info.news || "Fitness doubt") + chanceTxt,
                  action: () => navigate("/my-team"),
                });
              }
            });
          }
        } catch {}
      }

      if (alive) setAlerts(next);
    })();
    return () => {
      alive = false;
    };
  }, [fplId, navigate]);

  const visible = useMemo(
    () => alerts.filter((a) => !dismissed[a.id]),
    [alerts, dismissed]
  );

  const dismiss = (id) => {
    const d = { ...dismissed, [id]: Date.now() };
    setDismissed(d);
    saveDismissed(d);
  };

  const count = visible.length;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative text-white/90 hover:text-white text-xl p-1"
        title="Notifications"
        aria-label="Notifications"
      >
        🔔
        {count > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#dc143c] text-white text-[10px] font-bold grid place-items-center">
            {count > 9 ? "9+" : count}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 max-w-[90vw] bg-white text-gray-800 rounded-xl shadow-2xl border border-gray-200 z-50 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
            <span className="font-semibold text-sm">Notifications</span>
            {count > 0 && (
              <button
                onClick={() => {
                  const d = { ...dismissed };
                  visible.forEach((a) => (d[a.id] = Date.now()));
                  setDismissed(d);
                  saveDismissed(d);
                }}
                className="text-xs text-gray-400 hover:text-gray-700"
              >
                Clear all
              </button>
            )}
          </div>

          {count === 0 ? (
            <div className="px-4 py-8 text-center text-gray-400 text-sm">
              You're all caught up 🎉
              {!fplId && (
                <div className="mt-2 text-xs">
                  Add your FPL ID to get squad alerts.
                </div>
              )}
            </div>
          ) : (
            <ul className="max-h-96 overflow-y-auto divide-y divide-gray-100">
              {visible.map((a) => (
                <li
                  key={a.id}
                  className={`px-4 py-3 flex gap-3 hover:bg-gray-50 transition ${
                    a.kind === "urgent"
                      ? "bg-red-50/60"
                      : a.kind === "warn"
                      ? "bg-amber-50/50"
                      : ""
                  }`}
                >
                  <span className="text-lg leading-none mt-0.5">{a.icon}</span>
                  <div className="flex-1 min-w-0">
                    <button
                      onClick={() => {
                        a.action?.();
                        setOpen(false);
                      }}
                      className="text-left w-full"
                    >
                      <div
                        className={`text-sm font-semibold ${
                          a.kind === "urgent" ? "text-red-700" : "text-gray-800"
                        }`}
                      >
                        {a.title}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">{a.body}</div>
                    </button>
                  </div>
                  <button
                    onClick={() => dismiss(a.id)}
                    className="text-gray-300 hover:text-gray-600 text-sm self-start"
                    title="Dismiss"
                  >
                    ✕
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}